// @flow
import React from 'react'
import Scroller from 'mz-utils/scrollTo'
import Field from '../Field'
import { setIn } from './utils'
import {
  touchAllFields,
  untouchAllFields,
  getAllControllers,
} from '../Field/utils'

// Types
import type { Meta } from '../../core/BaseField'

type Props = {
  afterSubmit?: Function,
  beforeSubmit?: Function,
  onSubmit: Function,
  onChange?: Function,
  value?: any,
  meta?: Meta,
  onMetaChange?: Function,
  initValue?: any,
  render: Function,
  validate?: Function,
  validateOnSubmit?: boolean,
  scroller?: Scroller,
}
type State = {
  value: any,
  meta: Meta,
}

// Utils
function stopNativeSubmitEvent(event) {
  if (!event) return
  event.preventDefault && event.preventDefault()
  event.stopPropagation && event.stopPropagation()
}

/**
 * Simple wrapper around Field that implements boilerplate Form
 * functionality, such as keeping form value/meta, touchoing fields
 * on submit, handling submit loading status.
 * @extends React
 */
class Form extends React.Component<Props, State> {
  controller = null

  scroller = this.props.scroller || new Scroller()

  state = {
    value: { ...this.props.initValue },
    meta: {},
  }

  get value() {
    return this.props.value || this.state.value
  }

  get meta() {
    return this.props.meta || this.state.meta
  }

  scrollToFirstError = () => {
    if (!this.controller) return
    const flatControllers = getAllControllers(this.controller)
    let lowestY = +Infinity
    let firstFieldNode = null

    flatControllers.forEach((c) => {
      if (!c.fieldDomNode || !c.fieldDomNode.getBoundingClientRect) return
      const rect = c.fieldDomNode.getBoundingClientRect()

      if (rect.top < lowestY && !c.meta.valid && c.meta.touched) {
        lowestY = rect.top
        firstFieldNode = c.fieldDomNode
      }
    })

    if (firstFieldNode) {
      this.scroller.toElement(firstFieldNode, { offset: -10 })
    }
  }

  handleChangeValue = (value: any) => {
    if (!this.props.value) {
      this.setState({ value })
    }
    this.props.onChange && this.props.onChange(value)
  }

  handleChangeMeta = (meta: Meta) => {
    if (!this.props.meta) {
      this.setState({ meta })
    }
    this.props.onMetaChange && this.props.onMetaChange(meta)
  }

  handleSubmitEvent = (event: any) => {
    stopNativeSubmitEvent(event)

    if (this.meta.deepLoading) {
      // do nothing
    } else if (!this.meta.deepValid) {
      this.handleChangeMeta(touchAllFields(this.meta))
    } else {
      this.doSubmit(event)
    }
  }

  handleResetEvent = (event: any) => {
    stopNativeSubmitEvent(event)

    const emptyValue = { ...this.props.initValue }
    this.handleChangeMeta(untouchAllFields(this.meta))
    this.handleChangeValue(emptyValue)
  }

  handleChangeFieldValue = (name: string, value: any) => {
    const newValue = setIn(this.value, name, value)
    this.handleChangeValue(newValue)
  }

  doSubmit(event: any) {
    const { afterSubmit, validateOnSubmit } = this.props
    const { controller } = this

    if (!controller) return

    const runSubmit = () => {
      controller
        .submit((value, meta) => {
          const { onSubmit, beforeSubmit } = this.props
          beforeSubmit && beforeSubmit(value, meta, event)
          return onSubmit && onSubmit(value, meta, event)
        })
        .then((result) => {
          afterSubmit && afterSubmit(result, event)
        })
    }

    if (validateOnSubmit) {
      controller.deepValidate().then(() => {
        if (this.meta.deepValid) {
          runSubmit()
        }
      })
    } else {
      runSubmit()
    }
  }

  renderForm = ({ input, meta }: *, nest: *, opts: *, controller: *) => {
    this.controller = controller

    return this.props.render(
      {
        value: input.value,
        meta,
        reset: this.handleResetEvent,
        submit: this.handleSubmitEvent,
        change: this.handleChangeFieldValue,
        scrollToFirstError: this.scrollToFirstError,
      },
      nest
    )
  }

  render() {
    return (
      <Field
        input={{
          onChange: this.handleChangeValue,
          value: this.value,
        }}
        meta={this.meta}
        onMetaChange={this.handleChangeMeta}
        validate={this.props.validate}
        render={this.renderForm}
      />
    )
  }
}

export default Form
